import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  login as loginRequest,
  logout as logoutRequest,
  restoreSession,
  type AdminUser,
  type LoginCredentials,
} from "@/api/auth";
import { setSessionExpiredHandler } from "@/api/client";

interface AuthContextValue {
  user: AdminUser | null;
  /** True until the boot-time session restore has settled. */
  isRestoring: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<AdminUser>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

/**
 * Session state for the whole app.
 *
 * The access token lives in memory (src/api/client.ts), so a hard reload
 * starts with no token even though the refresh cookie may still be valid.
 * That is what `isRestoring` covers: on mount we try one refresh + profile
 * fetch, and the route guard waits rather than bouncing to /login.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AdminUser | null>(null);
  const [isRestoring, setIsRestoring] = React.useState(true);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    let cancelled = false;

    void restoreSession()
      .then((restored) => {
        if (!cancelled) setUser(restored);
      })
      .finally(() => {
        if (!cancelled) setIsRestoring(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // When a refresh fails mid-session the client clears the token and calls
  // this. Dropping the user here is what makes the guard redirect; clearing
  // the cache stops the next login seeing the previous admin's data.
  React.useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      queryClient.clear();
    });
    return () => setSessionExpiredHandler(() => {});
  }, [queryClient]);

  const login = React.useCallback(async (credentials: LoginCredentials) => {
    const loggedIn = await loginRequest(credentials);
    setUser(loggedIn);
    return loggedIn;
  }, []);

  const logout = React.useCallback(async () => {
    await logoutRequest();
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  const value = React.useMemo(
    () => ({
      user,
      isRestoring,
      isAuthenticated: user !== null,
      login,
      logout,
    }),
    [user, isRestoring, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return context;
}
